import HttpClient from "../../HttpClient";
import {
  INotificationFunctionKeys,
  IUserNotification,
  IUserNotificationRequest,
  IUserNotificationResponse,
} from "../../Interface/Shared/Notification";
import {
  appendUserNotifications,
  setNotificationFunctionKeys,
  setNotificationsTotal,
  setUnreadNotificationCount,
  setUserNotifications,
} from "../../Redux/Actions/Index";
import store from "../../Redux/Store/AppStore";
import apiRoutes from "../../Routes/Routes.json";
import { getTokenData } from "../../Utils/Auth";

/**
 * Fetches the notifications of the logged in user.
 * `pageNumber`/`pageSize` of 0 returns every notification of the user.
 */
const getAllUserNotifications = (request: IUserNotificationRequest) => {
  return HttpClient().post<IUserNotificationResponse>(
    `/${apiRoutes.UserManagement.GetAllUserNotifications}`,
    request
  );
};

/**
 * The endpoint has been seen returning the list at a couple of different
 * depths, so accept them all and always hand back an array.
 */
const extractNotifications = (responseData: any): IUserNotification[] => {
  if (!responseData) {
    return [];
  }
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.data?.notifications)) {
    return responseData.data.notifications;
  }
  if (Array.isArray(responseData?.notifications)) {
    return responseData.notifications;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  if (Array.isArray(responseData?.data?.data)) {
    return responseData.data.data;
  }
  return [];
};

const extractFunctionKeys = (responseData: any): INotificationFunctionKeys =>
  responseData?.data?.functionKeys ?? {};

/**
 * The list is paged while unreadCount covers every notification of the user,
 * so the badge has to come from the server rather than from the loaded page.
 */
const extractUnreadCount = (responseData: any): number =>
  Math.max(0, Number(responseData?.unreadCount) || 0);

/** How many notifications one page of the bell's infinite scroll holds */
export const NOTIFICATIONS_PAGE_SIZE = 10;

const extractTotal = (responseData: any): number =>
  Math.max(0, Number(responseData?.total) || 0);

/**
 * Loads the logged in user's notifications and writes them to Redux, together
 * with the per-operation Azure Function keys the SignalR calls need.
 * Called once right after login (before SignalR connects) and again whenever
 * the bell is opened.
 */
export const loadUserNotifications = async (
  pageNumber: number = 1,
  append: boolean = false
): Promise<{
  notifications: IUserNotification[];
  functionKeys: INotificationFunctionKeys;
  unreadCount: number;
  total: number;
}> => {
  const userId = getTokenData()?.userId ?? "";
  if (!userId) {
    return { notifications: [], functionKeys: {}, unreadCount: 0, total: 0 };
  }

  // Only userId is filtered on; every other field goes as null.
  // Paging is 1-based here, matching usePagination and the rest of the portal.
  const response = await getAllUserNotifications({
    pageNumber,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
    queryModel: {
      userId,
      messageTitle: null,
      message: null,
      link: null,
      isDownloadable: null,
      isRedirectable: null,
      seenAt: null,
      data: null,
      createdAt: null,
    },
    sortColumn: null,
    sortDirection: null,
  });

  const notifications = extractNotifications(response?.data);
  const functionKeys = extractFunctionKeys(response?.data);
  const unreadCount = extractUnreadCount(response?.data);
  const total = extractTotal(response?.data);

  store.dispatch(
    append
      ? appendUserNotifications(notifications)
      : setUserNotifications(notifications)
  );
  store.dispatch(setNotificationsTotal(total));
  store.dispatch(setUnreadNotificationCount(unreadCount));
  // Keys can rotate, so always overwrite what is in the store
  store.dispatch(setNotificationFunctionKeys(functionKeys));

  return { notifications, functionKeys, unreadCount, total };
};

const NotificationService = {
  getAllUserNotifications,
  loadUserNotifications,
};

export default NotificationService;
